import {
  Box,
  CircularProgress,
  FormControlLabel,
  Switch,
} from '@mui/material';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import BaseHighchart from '../../BaseHighchart';
import {
  ChartDataType,
  ChartFiltersWiseconn,
} from '../../types';
import { calculateSeries, prepareData } from '../builder';

import { SALINITY_NAME, STACKED_SOIL_MOISTURE_NAME, CHART_TITLE_SALINIDAD_APILADA } from '../../../../constants/graphs';
import apiClient from '../../../../requests/api/apiClient';
import { ConversationSchema, TimePointSchema } from '../../../../requests/api/apiTypes';
import { ReduxState } from '../../../../types';
import { navigateToChart } from '../../../../utils/helpers';
import { FetchError } from '../../../../views/types';
import ChartFilter from '../../ChartFilter';
import ConversationModal from '../../ConversationTab/conversationModal';
import { FormattedSeries } from '../types';
import {
  getChartConfig,
  getStackedTooltip,
  getYAxis,
} from '../utils';
import ChartConfigDialog from '../../ChartConfigDialog/ChartConfigDialog';
import SalinityConfigContent from './SalinityConfigContent';
import useChartConfigClick from '../../../../hooks/useChartConfigClick';

function SalinityChart({
  chartView,
  sectorId,
  farmId,
  hideLegend = false,
  hideXValues = false,
  hideYValues = false,
  height = 600,
  title = CHART_TITLE_SALINIDAD_APILADA,
  yAxisTitle = 'Salinidad (VIC)',
  externalModalState,
  externalSetModalState,
  externalConversations,
  externalSetConversations,
  onConversationCreated,
  isGeneratingPDF = false,
}: {
  chartView: 'dashboard' | 'graphs' | 'settings' | 'fullScreen'
  sectorId: string
  farmId: string
  hideLegend?: boolean
  hideXValues?: boolean
  hideYValues?: boolean
  height?: number | string
  title?: string
  yAxisTitle?: string
  externalModalState?: {
    open: boolean;
    conversationId: string;
    timePointData: TimePointSchema;
  };
  externalSetModalState?: React.Dispatch<React.SetStateAction<{
    open: boolean;
    conversationId: string;
    timePointData: TimePointSchema;
  }>>;
  externalConversations?: ConversationSchema[];
  externalSetConversations?: React.Dispatch<React.SetStateAction<ConversationSchema[]>>;
  onConversationCreated?: () => void;
  isGeneratingPDF?: boolean;
}) {
  const chartName = 'salinidad-suelo-apilado';
  const [chartData, setChartData] = useState<ChartDataType>({
    series: [],
    categories: [],
    absoluteCategories: [],
  });
  const [showOriginal, setShowOriginal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const [internalModalState, setInternalModalState] = useState<{
    open: boolean;
    conversationId: string;
    timePointData: TimePointSchema;
  }>({
    open: false,
    conversationId: '',
    timePointData: {
      hour: '',
      day: '',
      month: '',
      year: '',
    },
  });
  const [internalConversations, setInternalConversations] = useState<ConversationSchema[]>([]);

  const { interval } = useSelector((state: ReduxState) => state.graphsFilter);
  const selectedSector = useSelector((state: ReduxState) => state.currentSector.selectedSector);

  const modalState = externalModalState || internalModalState;
  const setModalState = externalSetModalState || setInternalModalState;
  const conversations = externalConversations || internalConversations;
  const setConversations = externalSetConversations || setInternalConversations;

  const transformData = useCallback(
    (
      preparedData: FormattedSeries[],
      shouldSubtractDepth: boolean,
    ) => {
      if (!interval) return;
      const seriesData = calculateSeries(
        preparedData,
        shouldSubtractDepth,
        interval.startDate,
        interval.endDate,
      );

      setChartData(seriesData);
    }, [interval],
  );

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      if (interval) {
        if (!selectedSector?.telemetryType) {
          setIsLoading(false);
          return;
        }

        const chartFilters: ChartFiltersWiseconn = {
          farmId,
          startDate: interval.startDate,
          endDate: interval.endDate,
          sensorType: 'Salinity',
          sectorId,
          chart: SALINITY_NAME,
        };

        const preparedData = await prepareData({
          ...chartFilters,
          sn: selectedSector?.telemetryType === 'sn' ? selectedSector?.sn : undefined,
        }, selectedSector?.telemetryType);

        transformData(preparedData, showOriginal);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [farmId,
    interval,
    showOriginal,
    transformData,
    selectedSector,
    sectorId,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const customTooltip = useMemo(() => ({
    formatter(this: Highcharts.TooltipFormatterContextObject) {
      return getStackedTooltip(this, conversations);
    },
  }), [showOriginal, conversations]);

  const chartConfig = useMemo(() => interval && getChartConfig(
    chartData,
    height,
    hideXValues,
    getYAxis(yAxisTitle, chartData.hideYValues || hideYValues) as Highcharts.YAxisOptions,
    dayjs(interval.startDate).valueOf(),
    dayjs(interval.endDate).valueOf(),
  ), [
    height,
    hideXValues,
    chartData,
    interval,
  ]);

  useEffect(() => {
    if (!externalConversations) {
      const loadConversations = async () => {
        try {
          const { data } = await apiClient.conversation.conversationDetail(
            sectorId,
            SALINITY_NAME,
          );
          setInternalConversations(data.conversations);
        } catch (err) {
          const { error } = err as FetchError;
          enqueueSnackbar(error.message, { variant: 'error' });
        }
      };

      loadConversations();
    }
  }, [sectorId, externalConversations]);

  const handleConfigClick = useChartConfigClick({
    chartView,
    route: chartName,
    farmId,
    sectorId,
    onFullScreen: () => setConfigDialogOpen(true),
  });

  const handleFullScreenClick = useCallback(() => {
    navigateToChart(`fullScreen/${chartName}`, history, {
      farmId,
      sectorId,
    });
  }, [history, farmId, sectorId]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={height}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <BaseHighchart
        chartView={chartView}
        sectorId={sectorId}
        graphName={SALINITY_NAME}
        filter={chartView === 'settings' ? <ChartFilter sectorId={sectorId} /> : undefined}
        title={title}
        yAxisTitle={showOriginal ? '' : yAxisTitle}
        series={chartData.series as Highcharts.SeriesOptionsType[]}
        tooltip={customTooltip}
        hideYValues={showOriginal || hideYValues}
        showFullScreen={true}
        legend={{ enabled: !hideLegend }}
        stackButton={isGeneratingPDF ? undefined : {
          onClick: () => setShowOriginal(!showOriginal),
          isStacked: showOriginal,
          component: (
            <FormControlLabel
              control={
                <Switch
                  checked={!showOriginal}
                  onChange={() => setShowOriginal(!showOriginal)}
                  sx={{
                    '& .MuiSwitch-switchBase': {
                      color: '#a8d4f7',
                      '&.Mui-checked': {
                        color: '#3693d5',
                      },
                      '&.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#3693d5',
                      },
                    },
                    '& .MuiSwitch-track': {
                      backgroundColor: '#a8d4f7',
                    },
                  }}
                />
              }
              label={showOriginal ? 'Original' : 'Apilado'}
              sx={{ mr: 2 }}
            />
          ),
        }}
        chartConfig={chartConfig}
        onConfigClick={handleConfigClick}
        onFullScreenClick={handleFullScreenClick}
        absoluteCategories={chartData.absoluteCategories}
        setModalState={setModalState}
        conversations={conversations}
        height={height}
        zoomSync={true}
        autoAdjustYAxis={true}
        interval={interval}
      />
      <ConversationModal
        modalState={modalState}
        setModalState={setModalState}
        farmId={farmId}
        sectorId={sectorId}
        chart={STACKED_SOIL_MOISTURE_NAME}
        setConversationsFunction={setConversations}
        onConversationCreated={onConversationCreated}
      />
      <ChartConfigDialog
        open={configDialogOpen}
        onClose={() => setConfigDialogOpen(false)}
        title="Configuración · Salinidad"
      >
        <SalinityConfigContent
          sectorId={sectorId}
          onConfigSaved={() => fetchData()}
        />
      </ChartConfigDialog>
    </>
  );
}

export default SalinityChart;
